import React from 'react';
import { Drawer, Grid, Box } from '@mui/material';
import { OfferListFilters, StatusFilter, SyncOn } from '@modules/offers/types';
import { IDropdownOptions } from '@shared/interface';
import { Select } from '@shared/components/Select/Select';
import { FilterAlt } from '@mui/icons-material';
import { DrawerHeader, DrawerContent, DrawerFooter } from '@shared/components/drawer';
import { SyncEnvironment } from '../AddEditOffer/SyncSection';

interface OfferFiltersDrawerProps {
  open: boolean;
  onClose: () => void;
  filters: OfferListFilters;
  onFilterChange: (filters: Partial<OfferListFilters>) => void;
  onReset: () => void;
  loading?: boolean;
  categoryOptions: IDropdownOptions[];
  currencyOptions: IDropdownOptions[];
  scopeOptions: IDropdownOptions[];
  countryOptions: IDropdownOptions[];
}

export const OfferFiltersDrawer: React.FC<OfferFiltersDrawerProps> = ({
  open,
  onClose,
  filters,
  onFilterChange,
  onReset,
  loading = false,
  categoryOptions,
  currencyOptions,
  scopeOptions,
  countryOptions,
}) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', md: 400 } } } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <DrawerHeader title="Filters" onClose={onClose} icon={<FilterAlt />} />

        <DrawerContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Select
                label="Status"
                value={filters.status || ''}
                onChange={(val) => onFilterChange({ status: (val as StatusFilter) || undefined })}
                options={[
                  { label: 'Running', value: 'running' },
                  { label: 'Upcoming', value: 'upcoming' },
                ]}
                disabled={loading}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Select
                label="Environment"
                value={filters.sync_on || ''}
                onChange={(val) => onFilterChange({ sync_on: (val as SyncOn) || undefined })}
                options={[
                  { label: 'Draft', value: SyncEnvironment.DRAFT },
                  { label: 'Sandbox', value: 'staging' },
                  { label: 'Production', value: SyncEnvironment.PRODUCTION },
                ]}
                disabled={loading}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Select
                label="Category"
                value={filters.category || ''}
                onChange={(val) => onFilterChange({ category: (val as number) || undefined })}
                options={categoryOptions}
                disabled={loading}
                useOptionId
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Select
                label="Currency"
                value={filters.currency || ''}
                onChange={(val) => onFilterChange({ currency: (val as number) || undefined })}
                options={currencyOptions}
                disabled={loading}
                useOptionId
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Select
                label="Platform"
                value={filters.scope || ''}
                onChange={(val) => onFilterChange({ scope: (val as number) || undefined })}
                options={scopeOptions}
                disabled={loading}
                useOptionId
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Select
                label="Country"
                value={filters.country || ''}
                onChange={(val) => onFilterChange({ country: (val as number) || undefined })}
                options={countryOptions}
                disabled={loading}
                useOptionId
              />
            </Grid>
          </Grid>
        </DrawerContent>

        <DrawerFooter
          primaryButton={{
            label: 'Reset Filters',
            onClick: () => {
              onReset();
              onClose();
            },
            disabled: loading,
            variant: 'outlined',
          }}
        />
      </Box>
    </Drawer>
  );
};
